import { Upload } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * A drag-and-drop target with a "Choose file" button, for any screen that
 * takes files from the user.
 *
 * Owns the drag highlight and the hidden `<input type="file">`. What a picked
 * file MEANS — validate it, stage it, upload it — is the caller's, through
 * `onFiles`; whatever should sit under the prompt (a staged file, a progress
 * line) is passed as children.
 *
 * Extracted from the import sheet so the invoice attachments panel does not
 * grow a second copy of the same drop target.
 */
export function FileDropZone({
  accept,
  hint,
  onFiles,
  multiple = false,
  disabled = false,
  icon,
  label = 'Drop a file here',
  buttonLabel = 'Choose file',
  className,
  children,
}: {
  /** The `accept` attribute. A hint to the picker, not a guarantee — dropped
   *  files ignore it, so `onFiles` must still check what it gets. */
  accept: string;
  hint: ReactNode;
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  label?: string;
  buttonLabel?: string;
  className?: string;
  children?: ReactNode;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const take = (list: FileList | null | undefined) => {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;
    onFiles(multiple ? files : files.slice(0, 1));
  };

  return (
    <section
      aria-label={label}
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed p-8 text-center transition-colors',
        dragging
          ? 'border-primary bg-primary/5'
          : 'border-muted-foreground/30 hover:border-muted-foreground/60',
        disabled && 'pointer-events-none opacity-60',
        className,
      )}
      onDragLeave={() => setDragging(false)}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (!disabled) take(e.dataTransfer.files);
      }}
    >
      {icon}
      <div className="text-sm">
        <p className="font-medium">{label}</p>
        <p className="text-muted-foreground">{hint}</p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="size-4" />
        {buttonLabel}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          take(e.target.files);
          // Cleared so choosing the same file again still fires `change`.
          e.target.value = '';
        }}
      />
      {children}
    </section>
  );
}
